/**
 * A link to where a tenant's audiences or conversions are set up in the admin app.
 *
 * Which page that is depends on a flag the admin app owns (see `adminSetupPages.ts`), and
 * the newer pages redirect to the org home when it is off. Every link to these two
 * screens goes through here so none of them carries a path of its own: the build pages
 * are class components and the overview and dialog are function components, which is why
 * this is a component and not a hook.
 *
 * Starts on the pages that need no flag and upgrades when the answer lands, the same as
 * the sidebar and the model's setup links, so a click during the round trip still opens
 * a working page.
 */
import React, { useEffect, useState } from 'react';

import {
  ADMIN_PAGES_UNRESOLVED,
  adminSetupUrls,
  fetchAdminPagesEnabled,
} from '../../utils/adminSetupPages';

export function AdminSetupLink({
  tenant,
  page,
  className,
  children,
}: {
  tenant: number | string;
  page: 'audience' | 'conversion';
  className?: string;
  children: React.ReactNode;
}) {
  const [enabled, setEnabled] = useState(ADMIN_PAGES_UNRESOLVED);

  useEffect(() => {
    let live = true;
    fetchAdminPagesEnabled(String(tenant)).then((answer) => {
      if (live) setEnabled(answer);
    });
    return () => {
      live = false;
    };
  }, [tenant]);

  return (
    <a
      href={adminSetupUrls(tenant, enabled)[page]}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
    >
      {children}
    </a>
  );
}

export default AdminSetupLink;
